import { ChevronRightRounded } from '@mui/icons-material';
import { Box, ButtonBase, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { designTokens } from '../tokens';
import { createFocusRingStyles, createMotionTransition } from '../interaction';

export type ListRowProps = {
  label: ReactNode;
  value?: ReactNode;
  description?: ReactNode;
  trailing?: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
  density?: 'standard' | 'compact';
  inset?: 'standard' | 'none';
  testId?: string;
};

/** 확정된 값과 보조 정보를 정렬하고, 이동 동작이 있는 행에만 버튼 표현을 제공합니다. */
export function ListRow({ label, value, description, trailing, icon, onClick, disabled = false, ariaLabel, density = 'standard', inset = 'standard', testId }: ListRowProps) {
  const hasValue = value !== undefined && value !== null && value !== '';
  const content = <>
    {icon ? <Box data-slot="icon" sx={{ display: 'grid', placeItems: 'center', flexShrink: 0, alignSelf: 'center' }}>{icon}</Box> : null}
    <Box sx={{
      flex: 1, minWidth: 0, display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr) fit-content(45%)', sm: hasValue ? 'minmax(0, 1fr) minmax(0, 0.8fr) fit-content(35%)' : 'minmax(0, 1fr) fit-content(45%)' },
      columnGap: `${designTokens.layout.listRowGap}px`, rowGap: `${designTokens.layout.listRowContentGap}px`, alignItems: 'center',
    }}>
      <Box data-slot="content" sx={{ gridColumn: 1, gridRow: 1, minWidth: 0 }}>
        <Typography component="div" sx={{ ...designTokens.typography.ui, fontWeight: 600, color: 'inherit', overflowWrap: 'anywhere' }}>{label}</Typography>
        {description ? <Typography component="div" sx={{ ...designTokens.typography.supporting, mt: `${designTokens.layout.listRowContentGap}px`, color: designTokens.color.secondaryText, overflowWrap: 'anywhere' }}>{description}</Typography> : null}
      </Box>
      {hasValue ? <Typography component="div" data-slot="value" sx={{
        ...designTokens.typography.ui, minWidth: 0,
        gridColumn: { xs: '1 / -1', sm: 2 }, gridRow: { xs: 2, sm: 1 },
        textAlign: { xs: 'left', sm: 'right' }, fontWeight: 500, overflowWrap: 'anywhere',
      }}>{value}</Typography> : null}
      {trailing ? <Box data-slot="trailing" sx={{
        minWidth: 0, maxWidth: '100%', gridColumn: { xs: 2, sm: hasValue ? 3 : 2 }, gridRow: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: `${designTokens.layout.listRowContentGap}px`,
      }}>{trailing}</Box> : null}
    </Box>
    {onClick ? <ChevronRightRounded data-slot="chevron" aria-hidden sx={{ color: designTokens.color.mutedText, flexShrink: 0 }} /> : null}
  </>;
  const rowSx = {
    width: '100%', minWidth: 0,
    minHeight: density === 'compact' ? designTokens.layout.listRowCompactMinHeight : designTokens.layout.listRowMinHeight,
    px: inset === 'none' ? 0 : `${designTokens.layout.listRowInlineInset}px`,
    py: `${density === 'compact' ? designTokens.layout.listRowCompactBlockInset : designTokens.layout.listRowBlockInset}px`,
    display: 'flex', gap: `${designTokens.layout.listRowGap}px`, alignItems: 'center', textAlign: 'left', color: designTokens.color.primaryText,
  } as const;
  return onClick ? <ButtonBase
    data-testid={testId} data-pattern="list-row" aria-label={ariaLabel} disabled={disabled} onClick={onClick}
    disableRipple
    sx={{
      ...rowSx,
      borderRadius: designTokens.radius.navigation,
      transition: createMotionTransition(['background-color']),
      '&:hover': { bgcolor: designTokens.color.hoverSurface },
      '&:focus-visible, &.Mui-focusVisible': createFocusRingStyles({ inset: true }),
      '&.Mui-disabled, &:disabled': { opacity: designTokens.button.disabledOpacity },
      '@media (forced-colors: active)': {
        '&.Mui-disabled, &:disabled': { opacity: 1, color: 'GrayText' },
      },
    }}
  >{content}</ButtonBase> : <Box data-testid={testId} data-pattern="list-row" aria-label={ariaLabel} sx={rowSx}>{content}</Box>;
}
